import { useAsync } from '@nxsys/hooks/useAsync';
import {
  getAgencyByIdApi,
  getAgencyInvoicingByIdApi,
  getAgencyInvoiceSettingByIdApi,
  getAgencyBankByIdApi,
  getAgencyMarginByIdApi,
  getAgencyTimesheetValueByIdApi,
  getAgencyAssignmentByIdApi,
  createAgencyV2Api,
  createAgencyBranchByIdApi,
  updateAgencyBranchByIdApi,
  createAgencyContactByIdApi,
  updateAgencyContactByIdApi,
  updateAgencyByIdApi,
  updateAgencyInvoicingByIdApi,
  updateAgencyInvoiceSettingByIdApi,
  updateAgencyMarginByIdApi,
  updateAgencyBankByIdApi,
  createAgencyTimesheetValueByIdApi,
  deleteAgencyTimesheetValueByIdApi,
  deleteAgencyContactByIdApi,
  deleteAgencySiteByIdApi,
} from '@nxsys/services/api/agency';
import {
  GetAgencyAssignmentByIdParams,
  GetAgencyAssignmentByIdResponse,
  GetAgencyBankByIdResponse,
  GetAgencyByIdResponse,
  GetAgencyInvoiceSettingByIdResponse,
  GetAgencyInvoicingByIdResponse,
  GetAgencyMarginByIdResponse,
  GetAgencyTimesheetValueByIdParams,
  GetAgencyTimesheetValueByIdResponse,
  CreateAgencyResponse,
  CreateAgencyBranchResponse,
  CreateAgencyBranchParams,
  UpdateAgencyBranchResponse,
  UpdateAgencyBranchParams,
  CreateAgencyContactResponse,
  CreateAgencyContactParams,
  UpdateAgencyContactResponse,
  UpdateAgencyContactParams,
  UpdateAgencyByIdResponse,
  UpdateAgencyByIdParams,
  UpdateAgencyInvoicingByIdResponse,
  UpdateAgencyInvoicingByIdParams,
  UpdateAgencyInvoiceSettingByIdResponse,
  UpdateAgencyInvoiceSettingByIdParams,
  UpdateAgencyMarginByIdResponse,
  UpdateAgencyMarginByIdParams,
  UpdateAgencyBankByIdResponse,
  UpdateAgencyBankByIdParams,
  CreateAgencyTimesheetValueByIdResponse,
  CreateAgencyTimesheetValueByIdParams,
  CreateAgencyPayload,
} from '@nxsys/types/api/agency';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useGetAgencyById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyByIdResponse,
    string
  >(getAgencyByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyInvoicingById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyInvoicingByIdResponse,
    string
  >(getAgencyInvoicingByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyInvoiceSettingById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyInvoiceSettingByIdResponse,
    string
  >(getAgencyInvoiceSettingByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyBankById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyBankByIdResponse,
    string
  >(getAgencyBankByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyMarginById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyMarginByIdResponse,
    string
  >(getAgencyMarginByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyTimesheetValueById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyTimesheetValueByIdResponse,
    [string, GetAgencyTimesheetValueByIdParams]
  >(([id, params]) => getAgencyTimesheetValueByIdApi(id, params));

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyAssignmentById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyAssignmentByIdResponse,
    GetAgencyAssignmentByIdParams & { id: string }
  >(({ id, ...params }) => getAgencyAssignmentByIdApi(id, params));

  const _run = (id: string, params: GetAgencyAssignmentByIdParams) => {
    run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateAgency = () => {
  const { value, loading, error, run } = useAsync<
    CreateAgencyResponse,
    CreateAgencyPayload
  >(createAgencyV2Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreateAgencyBranchById = () => {
  const { value, loading, error, run } = useAsync<
    CreateAgencyBranchResponse,
    CreateAgencyBranchParams & { id: string }
  >(({ id, ...params }) => createAgencyBranchByIdApi(id, params));

  const _run = async (id: string, params: CreateAgencyBranchParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyBranchById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyBranchResponse,
    UpdateAgencyBranchParams & { id: string }
  >(({ id, ...params }) => updateAgencyBranchByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyBranchParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateAgencyContactById = () => {
  const { value, loading, error, run } = useAsync<
    CreateAgencyContactResponse,
    CreateAgencyContactParams & { id: string }
  >(({ id, ...params }) => createAgencyContactByIdApi(id, params));

  const _run = async (id: string, params: CreateAgencyContactParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyContactById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyContactResponse,
    UpdateAgencyContactParams & { id: string }
  >(({ id, ...params }) => updateAgencyContactByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyContactParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyByIdResponse,
    UpdateAgencyByIdParams & { id: string }
  >(({ id, ...params }) => updateAgencyByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyInvoicingById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyInvoicingByIdResponse,
    UpdateAgencyInvoicingByIdParams & { id: string }
  >(({ id, ...params }) => updateAgencyInvoicingByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyInvoicingByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyInvoiceSettingById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyInvoiceSettingByIdResponse,
    UpdateAgencyInvoiceSettingByIdParams & { id: string }
  >(({ id, ...params }) => updateAgencyInvoiceSettingByIdApi(id, params));

  const _run = async (
    id: string,
    params: UpdateAgencyInvoiceSettingByIdParams,
  ) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyMarginById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyMarginByIdResponse,
    UpdateAgencyMarginByIdParams & { id: string }
  >(({ id, ...params }) => updateAgencyMarginByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyMarginByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateAgencyBankById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateAgencyBankByIdResponse,
    UpdateAgencyBankByIdParams & { id: string }
  >(({ id, ...params }) => updateAgencyBankByIdApi(id, params));

  const _run = async (id: string, params: UpdateAgencyBankByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateAgencyTimesheetValueById = () => {
  const { value, loading, error, run } = useAsync<
    CreateAgencyTimesheetValueByIdResponse,
    CreateAgencyTimesheetValueByIdParams & { id: string }
  >(({ id, ...params }) => createAgencyTimesheetValueByIdApi(id, params));

  const _run = async (
    id: string,
    params: CreateAgencyTimesheetValueByIdParams,
  ) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDeleteAgencyTimesheetValueById = () => {
  const { value, loading, error, run } = useAsync<{ success: boolean }, string>(
    deleteAgencyTimesheetValueByIdApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDeleteAgencyContactById = () => {
  const { value, loading, error, run } = useAsync<{ success: boolean }, string>(
    deleteAgencyContactByIdApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDeleteAgencySiteById = () => {
  const { value, loading, error, run } = useAsync<{ success: boolean }, string>(
    deleteAgencySiteByIdApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};
